import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Package, ChevronRight, Truck, Clock, CheckCircle, FileText, Briefcase } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useCurrency } from '../components/CurrencySelector';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const OrdersPage = () => {
  const { t, language, getLocalizedName } = useLanguage();
  const { format } = useCurrency();
  const { isAuthenticated, token } = useAuth();
  const navigate = useNavigate();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrders = async () => {
      if (!isAuthenticated) {
        setLoading(false);
        return;
      }
      
      try {
        const response = await axios.get(`${API}/orders`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setOrders(response.data);
      } catch (error) {
        console.error('Error fetching orders:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, [isAuthenticated, token]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'delivered':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'shipped':
        return <Truck className="w-5 h-5 text-blue-500" />;
      default:
        return <Clock className="w-5 h-5 text-yellow-500" />;
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="orders-page">
        <div className="text-center">
          <Package className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-2xl font-bold mb-2">{t('myOrders')}</h2>
          <p className="text-muted-foreground mb-6">
            {language === 'fr' ? 'Connectez-vous pour voir vos commandes' : 'Login to view your orders'}
          </p>
          <Button onClick={() => navigate('/login')} className="btn-primary">
            {t('login')}
          </Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container-main py-8">
        <Skeleton className="h-8 w-48 mb-8" />
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-32 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="orders-page-empty">
        <div className="text-center">
          <Package className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-2xl font-bold mb-2">
            {language === 'fr' ? 'Aucune commande' : 'No orders yet'}
          </h2>
          <p className="text-muted-foreground mb-6">
            {language === 'fr' ? 'Commencez vos achats!' : 'Start shopping!'}
          </p>
          <Button onClick={() => navigate('/shop')} className="btn-primary">
            {t('shopNow')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="orders-page">
      <div className="container-main py-8">
        <h1 className="text-3xl font-bold mb-8">{t('myOrders')}</h1>

        <div className="space-y-4">
          {orders.map((order) => (
            <div key={order.id} className="bg-white rounded-xl border border-border/40 overflow-hidden">
              {/* Order Header */}
              <div className="bg-slate-50 px-6 py-4 border-b border-border/40 flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-6">
                  <div>
                    <p className="text-xs text-muted-foreground">{t('orderDate')}</p>
                    <p className="font-medium">{new Date(order.created_at).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{t('total')}</p>
                    <p className="font-bold text-[#0F4C5C]">{format(order.total)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground font-mono">#{order.id.slice(0, 8)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {order.is_b2b && (
                    <Badge className="bg-[#E9C46A] text-[#0F4C5C] hover:bg-[#E9C46A]/90 font-bold border-[#0F4C5C]/10 flex items-center gap-1" data-testid={`b2b-badge-${order.id}`}>
                      <Briefcase className="w-3 h-3" />
                      B2B
                    </Badge>
                  )}
                  {getStatusIcon(order.status)}
                  <Badge className={
                    order.status === 'delivered' ? 'badge-success' :
                    order.status === 'shipped' ? 'badge-info' :
                    order.status === 'cancelled' ? 'badge-error' : 'badge-warning'
                  }>
                    {t(order.status) || order.status}
                  </Badge>
                </div>
              </div>

              {/* Order Items */}
              <div className="p-6">
                <div className="space-y-4">
                  {order.items?.slice(0, 3).map((item, idx) => (
                    <div key={idx} className="flex items-center gap-4">
                      <img
                        src={item.image || 'https://via.placeholder.com/60'}
                        alt={getLocalizedName(item)}
                        className="w-16 h-16 rounded-lg object-cover"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium line-clamp-1">{getLocalizedName(item)}</p>
                        <p className="text-sm text-muted-foreground">
                          {item.quantity} × {format(item.price)}
                        </p>
                      </div>
                      <p className="font-semibold">
                        {format(item.quantity * item.price)}
                      </p>
                    </div>
                  ))}
                  {order.items?.length > 3 && (
                    <p className="text-sm text-muted-foreground">
                      +{order.items.length - 3} {language === 'fr' ? 'autres articles' : 'more items'}
                    </p>
                  )}
                </div>
              </div>

              {/* Order Footer */}
              <div className="px-6 py-4 border-t border-border/40 flex justify-between items-center flex-wrap gap-2">
                <div className="text-sm text-muted-foreground">
                  {language === 'fr' ? 'Livraison:' : 'Shipping:'}{' '}
                  {order.shipping_address?.city}, {order.shipping_address?.country}
                  {order.relay_point?.name && (
                    <span className="block text-xs mt-0.5" data-testid={`relay-info-${order.id}`}>
                      {language === 'fr' ? 'Point relais' : 'Pickup point'}: {order.relay_point.name} ({order.relay_point.code})
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Link to={`/track/${order.id}`}>
                    <Button variant="outline" size="sm" data-testid={`track-page-btn-${order.id}`}>
                      <Truck className="w-4 h-4 mr-1.5" />
                      {language === 'fr' ? 'Suivi' : 'Track'}
                    </Button>
                  </Link>
                  {order.tracking?.tracking_number && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => order.tracking.tracking_url && window.open(order.tracking.tracking_url, '_blank')}
                      data-testid={`tracking-btn-${order.id}`}
                      className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                    >
                      <Truck className="w-4 h-4 mr-1.5" />
                      {language === 'fr' ? 'Suivre' : 'Track'} · {order.tracking.tracking_number}
                    </Button>
                  )}
                  {order.quote_id && (
                    <Link to="/quotes">
                      <Button variant="outline" size="sm" data-testid={`quote-source-btn-${order.id}`}>
                        <Briefcase className="w-4 h-4 mr-1.5" />
                        {language === 'fr' ? 'Devis source' : 'Source quote'}
                      </Button>
                    </Link>
                  )}
                  {order.is_b2b && order.payment_status === 'paid' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(`${API}/orders/${order.id}/po.pdf?auth=${encodeURIComponent(token)}`, '_blank')}
                      data-testid={`po-pdf-btn-${order.id}`}
                      className="border-[#E9C46A] text-[#0F4C5C] hover:bg-[#E9C46A]/10"
                    >
                      <FileText className="w-4 h-4 mr-1.5" />
                      {language === 'fr' ? 'Bon de commande (PDF)' : 'Purchase Order (PDF)'}
                    </Button>
                  )}
                  {order.payment_status === 'paid' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(`${API}/orders/${order.id}/invoice?auth=${encodeURIComponent(token)}&lang=${language}`, '_blank')}
                      data-testid={`invoice-btn-${order.id}`}
                    >
                      <FileText className="w-4 h-4 mr-1.5" />
                      {language === 'fr' ? 'Facture' : 'Invoice'}
                    </Button>
                  )}
                  <Button variant="ghost" size="sm">
                    {language === 'fr' ? 'Voir les détails' : 'View details'}
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
